import { KonvaEventObject } from 'konva/lib/Node'
import { useEffect, useState } from 'react'
import { Circle, Group, Line } from 'react-konva'
import { AnnotationLabel } from '../AnnotationLabel'
import { useDraggableGroup } from '../useDraggableGroup'
import { useDraggablePoint } from '../useDraggablePoint'

const CIRCLE_SIZE = 4

type PolygonProps = {
  name: string
  color: string
  initialPoints: number[][]
  draggable?: boolean
  isActivated?: boolean
  dashed?: boolean
  showLabel?: boolean
  onCoordinateChange: (points: number[][]) => void
}

export const Polygon = ({
  name,
  color,
  initialPoints,
  draggable = false,
  isActivated = false,
  dashed = false,
  showLabel = true,
  onCoordinateChange
}: PolygonProps) => {
  const [points, setPoints] = useState<number[][]>([
    [0, 0],
    [0, 0]
  ])

  useEffect(() => {
    setPoints(initialPoints)
  }, [initialPoints])

  const { handleGroupMouseOver, handleGroupDragStart, groupDragBound } =
    useDraggableGroup({ points })

  const { handlePointMouseOver, handlePointMouseOut, pointDragBoundFunc } =
    useDraggablePoint()

  const handlePointDragMove = (
    e: KonvaEventObject<DragEvent>,
    pointIndex: number
  ) => {
    setPoints([
      ...points.slice(0, pointIndex),
      [e.target.x() + 1, e.target.y() + 1],
      ...points.slice(pointIndex + 1)
    ])
  }

  const handleGroupDragEnd = (e: KonvaEventObject<DragEvent>) => {
    if (e.target.name() === name) {
      const result = [] as number[][]

      points.forEach((point) =>
        result.push([point[0] + e.target.x(), point[1] + e.target.y()])
      )

      e.target.position({ x: 0, y: 0 })

      setPoints(result)
      onCoordinateChange(result)
    }
  }

  return (
    <Group
      name={name}
      draggable={draggable}
      dragBoundFunc={(pos) => groupDragBound(pos)}
      onDragEnd={handleGroupDragEnd}
      onMouseOver={handleGroupMouseOver}
      onDragStart={handleGroupDragStart}
    >
      <Line
        points={points.flat()}
        stroke={color}
        strokeWidth={isActivated ? 1.5 : 1}
        fill={isActivated ? 'rgba(140,30,255,0.15)' : 'rgba(140,30,255,0.1)'}
        dash={dashed ? [4, 3] : []}
        closed
      />

      {points.map((point, index) => {
        const x = point[0] - 1
        const y = point[1] - 1

        return (
          <Circle
            key={index}
            x={x}
            y={y}
            radius={CIRCLE_SIZE}
            fill={isActivated ? 'white' : 'transparent'}
            stroke={isActivated ? color : 'transparent'}
            strokeWidth={2}
            draggable={draggable}
            dragBoundFunc={(pos) => pointDragBoundFunc(pos, CIRCLE_SIZE)}
            onDragMove={(e) => handlePointDragMove(e, index)}
            onDragEnd={() => onCoordinateChange(points)}
            onMouseOver={isActivated ? handlePointMouseOver : undefined}
            onMouseOut={isActivated ? handlePointMouseOut : undefined}
          />
        )
      })}

      {showLabel && (
        <AnnotationLabel
          x={points[0][0]}
          y={points[0][1] - 24}
          color="rgba(0,0,0,0.8)"
          textColor="white"
          text={name}
        />
      )}
    </Group>
  )
}
